interface SEOMeta {
  name?: string;
  property?: string;
  content: string;
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  ogUrl,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd,
  noindex = false,
}: SEOProps) {
  const siteUrl =
    import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.beer";
  const resolvedOgUrl = ogUrl || canonicalUrl || siteUrl;

  const metas: SEOMeta[] = [
    { name: "description", content: description },
  ];

  if (keywords && keywords.trim()) {
    metas.push({ name: "keywords", content: keywords });
  }

  if (noindex) {
    metas.push({ name: "robots", content: "noindex, nofollow" });
  }

  metas.push(
    { property: "og:title", content: ogTitle || title },
    { property: "og:description", content: ogDescription || description },
    { property: "og:url", content: resolvedOgUrl },
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: "ACBuySpreadsheet" },
  );

  if (ogImage) {
    metas.push({ property: "og:image", content: ogImage });
  }

  metas.push(
    { name: "twitter:card", content: twitterCard },
    { name: "twitter:title", content: twitterTitle || ogTitle || title },
    { name: "twitter:description", content: twitterDescription || ogDescription || description },
  );

  if (twitterImage || ogImage) {
    metas.push({ name: "twitter:image", content: twitterImage || ogImage || "" });
  }

  const jsonLdArray = (() => {
    if (!jsonLd) return null;
    if (Array.isArray(jsonLd)) return jsonLd;
    return [jsonLd];
  })();

  return (
    <>
      <title>{title}</title>
      {metas.map((meta, i) => (
        <meta key={i} {...(meta.name ? { name: meta.name } : {})} {...(meta.property ? { property: meta.property } : {})} content={meta.content} />
      ))}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {jsonLdArray &&
        jsonLdArray.map((ld, i) => (
          <script
            key={i}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(ld, null, 2) }}
          />
        ))}
    </>
  );
}